<template>
    <div class="club-fee-assignment-container">
        <kgr-loading v-show="loading" />
        <div v-show="!loading">
            <div>
                <div
                    class="kgr-alert kgr-alert--info kgr-row"
                >
                    {{ tInfoText }}
                </div>
                <div
                    class="kgr-alert kgr-alert--info kgr-row"
                >
                    {{ tAssignmentHint }}
                </div>
                <kgr-form-row
                    :label="tLabelClubFeeCategory"
                    :error="error"
                    field-name="clubFeeCategory"
                >
                    <kgr-club-fee-category-select
                        v-model="selectedCategory"
                        :club-id="clubId"
                        hide-label
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="tLabelClubFee"
                    :error="error"
                    field-name="clubFee"
                >
                    <kgr-club-fee-select
                        v-model="batchAssignment.clubFee"
                        :club-id="clubId"
                        :filter-by-category="selectedCategory"
                        hide-label
                        @post-input="onClubFeeChange"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="tLabelStart"
                    :error="error"
                    field-name="start"
                >
                    <div
                        class="form-widget date-picker"
                        :class="{ invalid: (!batchAssignment.start) }"
                    >
                        <kgr-date-picker v-model="batchAssignment.start" />
                    </div>
                </kgr-form-row>
                <kgr-form-row
                    v-if="!isOneTimeClubFee"
                    :label="tLabelEnd"
                    :error="error"
                    field-name="end"
                >
                    <div
                        class="form-widget date-picker"
                    >
                        <kgr-date-picker v-model="batchAssignment.end" />
                    </div>
                </kgr-form-row>
                <kgr-note-box
                    v-if="isRollingIntervalClubFee"
                    :text-key="'Lists.actions.ClubFeeAssignment.rollingIntervalHint'"
                    class="kgr-row"
                />
                <kgr-form-row
                    v-if="isOneTimeClubFee"
                    :label="tLabelQuantity"
                    :error="error"
                    field-name="quantityFactor"
                >
                    <kgr-generic-select
                        v-model="batchAssignment.quantityFactor"
                        :options="quantityFactorSelect"
                        no-null-option
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="tLabelNote"
                    :error="error"
                    field-name="note"
                >
                    <kgr-text-input
                        v-model="batchAssignment.note"
                        :maxlength="255"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="tLabelDiscount"
                    :error="error"
                    field-name="discount"
                >
                    <kgr-discount-select
                        v-model="batchAssignment.discount"
                        :club-id="clubId"
                        hide-label
                    />
                </kgr-form-row>
                <kgr-form-row
                    v-if="isFixedPriceDiscount"
                    :label="tLabelFixedPriceDiscount"
                    :error="error"
                    field-name="fixedPriceDiscount"
                >
                    <kgr-number-input
                        v-model="batchAssignment.fixedPriceDiscount"
                        allow-null
                    />
                    <div class="font-weight-light">
                        {{ tVatHint }}
                    </div>
                </kgr-form-row>
                <kgr-form-row
                    v-if="isFixedPriceDiscount"
                    :label="tLabelFixedPriceDiscountNetOrGross"
                    :error="error"
                    inline
                    field-name="fixedPriceDiscountNetOrGross"
                >
                    <kgr-radio
                        v-model="batchAssignment.fixedPriceDiscountNetOrGross"
                        :inline="true"
                        :options="vatOptions"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="tLabelSettleWithCredit"
                    :error="error"
                    field-name="settleWithCredit"
                >
                    <kgr-checkbox
                        v-model="batchAssignment.settleWithCredit"
                        :label="tLabelSettleWithCredit"
                    />
                </kgr-form-row>
                <hr class="kgr-separator" />
                <div class="kgr-row">
                    <button
                        class="kgr-button"
                        @click="$emit('onClose')"
                    >
                        {{ tButtonCancel }}
                    </button>
                    <button
                        class="kgr-button"
                        :disabled="!isValidData"
                        :value="tButtonExecuteTestRun"
                        @click="assignClubFees(true)"
                    >
                        {{ tButtonExecuteTestRun }}
                    </button>
                    <button
                        class="kgr-button kgr-button--danger"
                        :disabled="!isValidData"
                        :value="tButtonExecute"
                        @click="assignClubFees(false)"
                    >
                        {{ tButtonExecute }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BatchClubFeeAssignment from './BatchClubFeeAssignment.js';
import Checkbox from '../../../../../common/form/checkbox/Checkbox.vue';
import ClubDiscount from '../../../../../../js/dto/ClubDiscount.js';
import ClubDiscountSelect from '../../../../../common/form/select/ClubDiscountSelect.vue';
import ClubFeeCategorySelect from '../../../../../common/form/select/ClubFeeCategorySelect.vue';
import ClubFeeSelect from '../../../../../common/form/select/ClubFeeSelect.vue';
import DatePicker from '../../../../../common/form/picker/DatePicker.vue';
import Error from '../../../../../../js/model/Error.js';
import FormRow from '../../../../../common/form/FormRow.vue';
import GenericRadio from '../../../../../common/form/radio/GenericRadio.vue';
import GenericSelect from '../../../../../common/form/select/GenericSelect.vue';
import Loading from '../../../../../common/Loading.vue';
import NoteBox from '../../../../../common/NoteBox.vue';
import NumberInput from '../../../../../common/form/input/NumberInput.vue';
import TextInput from '../../../../../common/form/text-input/TextInput.vue';

export default {
    components: {
        'kgr-club-fee-select': ClubFeeSelect,
        'kgr-club-fee-category-select': ClubFeeCategorySelect,
        'kgr-discount-select': ClubDiscountSelect,
        'kgr-date-picker': DatePicker,
        'kgr-form-row': FormRow,
        'kgr-loading': Loading,
        'kgr-checkbox': Checkbox,
        'kgr-text-input': TextInput,
        'kgr-radio': GenericRadio,
        'kgr-number-input': NumberInput,
        'kgr-note-box': NoteBox,
        'kgr-generic-select': GenericSelect,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        listId: {
            type: Number,
            required: true,
        },
        entityIds: {
            type: Array,
            required: true,
        },
        loading: {
            type: Boolean,
            required: true,
        },
        savedActionData: {
            type: Object,
            required: false,
            default: () => null
        }
    },
    data() {
        return {
            batchAssignment: new BatchClubFeeAssignment(),
            error: new Error(),
            vatOptions: {
                'Lists.actions.ClubFeeAssignment.form.vatGross': 'gross',
                'Lists.actions.ClubFeeAssignment.form.vatNet': 'net',
            },
            quantityFactorSelect: [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20],
            selectedCategory: null
        };
    },
    computed: {
        tButtonCancel() {
            return this.$t('cancel');
        },
        tInfoText() {
            return this.$t('Lists.actions.ClubFeeAssignment.infoText', {count: this.entityIds.length });
        },
        tAssignmentHint() {
            return this.$t('Lists.actions.ClubFeeAssignment.extraHint');
        },
        tLabelClubFee() {
            return this.$t('Lists.actions.ClubFeeAssignment.form.clubFee');
        },
        tLabelClubFeeCategory() {
            return this.$t('Lists.actions.ClubFeeAssignment.form.clubFeeCategory');
        },
        tLabelNote() {
            return this.$t('Lists.actions.ClubFeeAssignment.form.note');
        },
        tLabelStart() {
            return this.$t('Lists.actions.ClubFeeAssignment.form.start');
        },
        tLabelEnd() {
            return this.$t('Ende');
        },
        tLabelDiscount() {
            return this.$t('Lists.actions.ClubFeeAssignment.form.discount');
        },
        tLabelFixedPriceDiscount() {
            return this.$t('Lists.actions.ClubFeeAssignment.form.fixedPriceDiscount');
        },
        tLabelFixedPriceDiscountNetOrGross() {
            return this.$t('Lists.actions.ClubFeeAssignment.form.fixedPriceDiscountNetOrGross');
        },
        tLabelSettleWithCredit() {
            return this.$t('Lists.actions.ClubFeeAssignment.form.settleWithCredit');
        },
        tLabelQuantity() {
            return this.$t('Lists.actions.ClubFeeAssignment.form.quantityFactor');
        },
        tButtonExecute() {
            return this.$t('Ausführen');
        },
        tButtonExecuteTestRun() {
            return this.$t('Lists.actions.executeTestRun');
        },
        tVatHint() {
            return this.$t('Lists.actions.ClubFeeAssignment.form.vatHint', {vat: this.fixedPriceDiscountVat});
        },
        isOneTimeClubFee() {
            if(!this.batchAssignment.clubFee) {
                return false
            }
            return this.batchAssignment.clubFee.basePrice?.type === 'oneTime';
        },
        isRollingIntervalClubFee() {
            if(!this.batchAssignment.clubFee) {
                return false
            }
            return this.batchAssignment.clubFee.rollingIntervalType !== null;
        },
        fixedPriceDiscountVat() {
            if(!this.batchAssignment.clubFee) {
                return null;
            }
            const vat = this.batchAssignment.clubFee.basePrice.vat;
            return (vat.numerator / vat.denominator * 100).toFixed(2);
        },
        isFixedPriceDiscount() {
            if(!this.batchAssignment.discount) {
                return false
            }
            return this.batchAssignment.discount.id === ClubDiscount.INDIVIDUAL_DISCOUNT;
        },
        isValidData() {
            return this.batchAssignment.isValid();
        },
    },
    mounted() {
        this.assignDefaultData();
    },
    updated() {
        this.assignDefaultData();
    },
    methods: {
        onClubFeeChange(clubFee) {
            this.batchAssignment.note = clubFee != null ? clubFee.note : '';
        },
        assignClubFees(isTestRun) {
            if(!this.isValidData)  {
                return;
            }
            this.batchAssignment.clubFeeVat = this.fixedPriceDiscountVat;
            this.$emit('executeAction', this.batchAssignment, isTestRun);
        },
        assignDefaultData() {
            this.batchAssignment.ids = this.entityIds;
            if(this.savedActionData !== null && this.savedActionData instanceof BatchClubFeeAssignment) {
                this.batchAssignment = this.savedActionData
            }
        }
    },
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
.club-fee-assignment-container {
    max-width: 500px;
}
</style>
